import { computed, Injectable, Signal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { CountDown } from "app/core";
import type { User } from "app/user";
import { BehaviorSubject, Observable } from "rxjs";
import type {
  AuthFailureReason,
  AuthRequestMode,
} from "../../../loader-factory/src/models/auth.model";
import { AuthState } from "./auth.model";

/** Stores authentication state and exposes read-only projections for the application. */
@Injectable({ providedIn: "root" })
export class AuthStore {
  private static readonly MAX_CONSECUTIVE_FAILURES = 3;

  private readonly stateSubject = new BehaviorSubject<AuthState>({ status: "idle" });
  private readonly consecutiveFailures = new CountDown(AuthStore.MAX_CONSECUTIVE_FAILURES);
  private readonly attemptsExhaustedSignal = signal(false);

  public readonly state$: Observable<AuthState> = this.stateSubject.asObservable();
  public readonly state: Signal<AuthState> = toSignal(this.state$, {
    requireSync: true,
  });
  public readonly isPending: Signal<boolean> = computed(
    () => this.state().status === "authenticating"
  );
  public readonly shouldPrompt: Signal<boolean> = computed(() => {
    const status = this.state().status;
    return status === "prompt" || status === "failed";
  });
  public readonly attemptsExhausted: Signal<boolean> = this.attemptsExhaustedSignal.asReadonly();

  public constructor() {
    this.consecutiveFailures.recharge();
  }

  /** Marks authentication as unnecessary for the current application configuration. */
  public markNotRequired(): void {
    this.stateSubject.next({ status: "not-required" });
  }

  /** Marks that a session id is being verified; conversation access waits until a terminal state. */
  public startVerification(): void {
    this.stateSubject.next({ status: "verifying" });
  }

  /** Starts a new authentication attempt and clears any previous failure state. */
  public startAuthentication(mode: AuthRequestMode): void {
    this.stateSubject.next({ status: "authenticating", mode });
  }

  /** Stores the verified user after authentication completes successfully and recharges retry attempts. */
  public markAuthenticated(user: User): void {
    this.consecutiveFailures.recharge();
    this.attemptsExhaustedSignal.set(false);
    this.stateSubject.next({ status: "authenticated", user });
  }

  /** Recharges the consecutive failure budget and resets exhaustion status. */
  public rechargeAttempts(): void {
    this.consecutiveFailures.recharge();
    this.attemptsExhaustedSignal.set(false);
  }

  /**
   * Stores a terminal authentication failure or transitions silent auth to the prompt.
   * Consumes one retry budget unit on terminal failures. With a budget of 3, the first 3
   * consecutive failures keep attemptsExhausted as false, and the 4th consecutive failure sets it to true.
   */
  public markFailure(mode: AuthRequestMode, reason: AuthFailureReason, detail?: string): void {
    const failureDetails = detail === undefined ? {} : { detail };

    if (mode === "silent" && reason === "interaction-required") {
      this.stateSubject.next({ status: "prompt", mode, reason, ...failureDetails });
      return;
    }

    const hasBudgetLeft = this.consecutiveFailures.countDown();
    this.attemptsExhaustedSignal.set(!hasBudgetLeft);

    this.stateSubject.next({ status: "failed", mode, reason, ...failureDetails });
  }
}
